import {
  IssueStatusPill,
  issueStatusLabel,
} from "@/components/issues/issue-status-pill/issue-status-pill";
import type { IssueStatus } from "@/types/api";

interface IssueStatusTriggerProps {
  status: IssueStatus;
  /** Passed through to the pill, so the button is exactly the size it replaces. */
  size?: "default" | "sm";
  onClick: () => void;
}

/**
 * The status pill on a desktop issue row, as a button that opens the update
 * sheet. Only rendered when the viewer may post an update; the pill itself
 * stays presentational. See [[issue-status-trigger.spec]].
 */
export function IssueStatusTrigger({
  status,
  size = "default",
  onClick,
}: IssueStatusTriggerProps) {
  // A closed issue's one update is a reopen, and the sheet opens in that form.
  const action = status === "closed" ? "Reopen issue" : "Update status";

  return (
    <button
      type="button"
      onClick={(event) => {
        // The trigger sits inside the row's link. Neither the side panel's
        // open-in-place handler nor the router may read this as opening the
        // issue — the click belongs to the sheet alone.
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }}
      aria-label={`${issueStatusLabel(status)}, ${action.toLowerCase()}`}
      title={action}
      className="inline-flex shrink-0 cursor-pointer rounded-full transition-shadow hover:ring-2 hover:ring-ring/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <IssueStatusPill status={status} size={size} />
    </button>
  );
}
